import type { CharacterRepository } from '../persistence/characterRepository.ts';
import type { SessionRepository } from '../persistence/sessionRepository.ts';
import type { RuntimeRepository } from '../persistence/runtimeRepository.ts';
import type { StoragePort } from '../persistence/storagePort.ts';

/**
 * Deleting a crawler, completely.
 *
 * A crawler is not one file. Over time it accumulates:
 *
 *   characters/<id>.json   the sheet
 *   characters/index.json  the library entry
 *   sessions/<id>.json     the sitting, if one was left open
 *   runtime/<id>.json      active Debuffs and cooldowns
 *   journals/<id>.json     a checkpoint journal, if a save was interrupted
 *
 * `CharacterRepository.delete` removes the first two, because that is all a
 * character repository should know about. Deleting only those left the rest
 * behind, and the ID is derived from the crawler - so making a new crawler that
 * happened to reuse the ID would inherit a stranger's conditions, or a stale
 * journal would be replayed over it at the next start.
 *
 * This service exists so there is ONE place that knows what a crawler is made
 * of. The repositories stay ignorant of each other: a character repository that
 * had to reach into sessions, runtime and journals would be the wrong shape,
 * and every future per-crawler file would have to be wired into it.
 *
 * The Character's own backup is kept: `CharacterRepository.delete` copies the
 * sheet into `backups/` before removing it, so a deletion by mistake is still
 * recoverable. The auxiliary files are not backed up, and deliberately so -
 * they are current state, not history, and restoring an old encounter over a
 * repaired crawler would do more harm than losing it.
 */

export interface CrawlerDeletionDeps {
  characters: CharacterRepository;
  sessions: SessionRepository;
  runtimes: RuntimeRepository;
  /** The journal has no repository of its own; the committer owns writing it. */
  storage: StoragePort;
}

export interface CrawlerDeletionResult {
  /** Which pieces were actually removed, for reporting and for tests. */
  removed: string[];
}

/**
 * Removes a crawler and everything that belongs only to it.
 *
 * The auxiliary files are removed FIRST, and the sheet last. If something fails
 * part way, what remains is a crawler with less state than it had rather than
 * orphaned state with no crawler - the second is the case that causes trouble
 * later, because nothing would ever look at it again to clean it up.
 *
 * Every step is safe when the file does not exist. Most crawlers have never had
 * a journal, and one that was never played has no session or runtime, so a
 * missing file is the normal case rather than an error.
 */
export async function deleteCrawler(
  characterId: string,
  deps: CrawlerDeletionDeps,
): Promise<CrawlerDeletionResult> {
  const removed: string[] = [];

  // Journal first. A journal replays itself at the next start, so a leftover
  // one would recreate the character that was just deleted.
  await deps.storage.deleteJournal(characterId);
  removed.push('journal');

  // `clear` and `end` are the repositories' own words for removing the file.
  await deps.runtimes.clear(characterId);
  removed.push('runtime');

  await deps.sessions.end(characterId);
  removed.push('session');

  // Last, and the only one that keeps a backup.
  await deps.characters.delete(characterId);
  removed.push('character');

  return { removed };
}
